import React, { useState, useEffect, useMemo } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Layout, Card, Descriptions, Spin, Result, Typography, theme, Space, Button, App, Row, Col, Statistic, Divider } from 'antd';
import {
  CalculatorOutlined,
  HomeOutlined,
  PlusCircleOutlined,
  DollarOutlined,
  PercentageOutlined,
  RiseOutlined,
  ShoppingCartOutlined,
  PlayCircleOutlined,
  TagOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { claimShare, fetchShareImage, fetchSharedResult, logRequestFailure, transportErrorOf } from '../../shared/api/client';
import { useAuthStore } from '../../shared/session/authStore';
import { SHARE_HANDOFF_KEY } from '../../shared/session/shareHandoff';
import { calcMarketingAmount, computeCogs, computeFullUnitCost, sumBudget, sumCategoryItems, type BudgetCategory } from '../../shared/lib/budget';
import { mapCalcData } from '../../shared/lib/resultMapping';
import { useAppColors } from '../../shared/theme';
import ProductThumb from '../../shared/ui/ProductThumb';

const HEADER_THUMB_SIZE = 72;

const SharedView: React.FC = () => {
  const { t } = useTranslation();
  const { shareId } = useParams<{ shareId: string }>();
  const { token } = theme.useToken();
  const c = useAppColors();
  const { message } = App.useApp();
  const { isAuthenticated } = useAuthStore();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [data, setData] = useState<Record<string, unknown> | null>(null);
  const [claiming, setClaiming] = useState(false);
  const [claimed, setClaimed] = useState(false);
  const [imageUrl, setImageUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!shareId) return;
    setLoading(true);
    fetchSharedResult(shareId)
      .then((res) => {
        setData(res);
      })
      .catch((e) => {
        logRequestFailure('Share load error:', e);
        // Клиент нормализует отказ в ApiError; статус и тело ответа живут в
        // исходной транспортной ошибке под ним.
        const status = transportErrorOf(e)?.response?.status;
        if (status === 404) {
          setError(t('idea_calc.shared_view.error_not_found'));
        } else if (status === 503) {
          setError(t('idea_calc.shared_view.error_db_unavailable'));
        } else {
          setError(t('idea_calc.shared_view.error_generic'));
        }
      })
      .finally(() => setLoading(false));
    // Фото — отдельным запросом: промах кэша идёт в Keepa, и расчёт не должен его ждать.
    setImageUrl(null);
    fetchShareImage(shareId)
      .then(setImageUrl)
      .catch((e: unknown) => logRequestFailure('Share image load error:', e));
  }, [shareId]);

  const handleClaim = async () => {
    if (!shareId) return;
    setClaiming(true);
    try {
      await claimShare(shareId);
      setClaimed(true);
      message.success(t('idea_calc.shared_view.msg_added_success'));
    } catch {
      message.error(t('idea_calc.shared_view.msg_added_error'));
    } finally {
      setClaiming(false);
    }
  };

  const handleLoadIntoCalculator = () => {
    if (!shareId) return;
    sessionStorage.setItem(SHARE_HANDOFF_KEY, shareId);
    navigate('/');
  };

  const calcData = (data?.calculation_data as Record<string, unknown>) || data || {};
  const profitData = (data?.profitability as Record<string, unknown>) || {};
  /**
   * Категории бюджета из сохранённой записи.
   *
   * Форм две: калькулятор шлёт МАССИВ категорий (`buildBackendPayload`), а записи
   * прежних версий несут `{ categories: [...] }`. Раньше читалась только вторая —
   * то есть у свежих расчётов блок бюджета на шар-странице не появлялся вовсе.
   */
  const budgetCategories = useMemo<BudgetCategory[]>(() => {
    const raw = data?.budget_data;
    if (Array.isArray(raw)) return raw as BudgetCategory[];
    const wrapped = (raw as { categories?: BudgetCategory[] } | undefined)?.categories;
    return Array.isArray(wrapped) ? wrapped : [];
  }, [data]);

  const fmt = (v: unknown) => {
    if (v === null || v === undefined) return '—';
    if (typeof v === 'number') return v.toLocaleString('en-US', { maximumFractionDigits: 2 });
    return String(v);
  };

  const fmtMoney = (v: unknown) => {
    if (v === null || v === undefined) return '—';
    if (typeof v === 'number') return `$${v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    return String(v);
  };

  /**
   * Себестоимость юнита из того же расчёта — она нужна маркетинговым строкам.
   *
   * У выкупов, отзывов и vine поле `amount` равно нулю по построению: их цена
   * считается из количества и себестоимости юнита (`calcMarketingAmount`). Пока
   * здесь суммировались голые `amount`, получатель ссылки видел бюджет без всего
   * маркетинга — на дефолтных строках это занижение примерно на 40 %.
   */
  const unitCosts = useMemo(() => {
    const calc = mapCalcData(calcData);
    if (!calc) return null;
    const inputs = {
      purchasePrice: calc.purchasePrice || 0,
      shippingPrice: (calcData.shipping_price as number) || 0,
      sellingPrice: calc.sellingPrice || 0,
      referralFee: calc.referralFee || 0,
      fbaFee: calc.fbaFee || 0,
      otherFees: calc.otherFees || 0,
      storageFee: calc.storageFee || 0,
    };
    return { cogs: computeCogs(inputs), fullUnitCost: computeFullUnitCost(inputs) };
  }, [calcData]);

  const budgetTotal = useMemo(
    () => (unitCosts ? sumBudget(budgetCategories, unitCosts.cogs, unitCosts.fullUnitCost) : 0),
    [budgetCategories, unitCosts],
  );

  return (
    <Layout style={{ minHeight: '100vh', background: token.colorBgLayout }}>
      <Layout.Header
        style={{
          background: c.brandBar,
          display: 'flex',
          alignItems: 'center',
          height: 64,
          padding: '0 24px',
          gap: 12,
        }}
      >
        <CalculatorOutlined style={{ fontSize: 24, color: c.info }} />
        <Typography.Title level={4} style={{ color: c.onFilled, margin: 0 }}>
          {t('idea_calc.shared_view.header_title')}
        </Typography.Title>
        <div style={{ flex: 1 }} />
        <Link to="/" style={{ color: c.info, display: 'flex', alignItems: 'center', gap: 4 }}>
          <HomeOutlined /> {t('idea_calc.shared_view.back_to_calculator')}
        </Link>
      </Layout.Header>
      <Layout.Content style={{ maxWidth: 900, width: '100%', margin: '24px auto', padding: '0 16px' }}>
        {loading && (
          <div style={{ textAlign: 'center', padding: 80 }}>
            <Spin size="large" />
          </div>
        )}
        {error && <Result status="warning" title={error} />}
        {!loading && !error && data && (
          <Space direction="vertical" size={16} style={{ width: '100%' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
              <ProductThumb
                src={imageUrl}
                size={HEADER_THUMB_SIZE}
                alt={String(data.parent_asin ?? data.fileName ?? '')}
              />
              {data.fileName ? (
                <Typography.Title level={3} style={{ margin: 0 }}>{String(data.fileName)}</Typography.Title>
              ) : null}
            </div>

            <Space size={8}>
              <Button
                type="primary"
                icon={<PlayCircleOutlined />}
                onClick={handleLoadIntoCalculator}
              >
                {t('idea_calc.shared_view.load_into_calculator')}
              </Button>
              {isAuthenticated && (
                <>
                  {!claimed ? (
                    <Button
                      icon={<PlusCircleOutlined />}
                      loading={claiming}
                      onClick={handleClaim}
                    >
                      {t('idea_calc.shared_view.add_to_history')}
                    </Button>
                  ) : (
                    <Button disabled>{t('idea_calc.shared_view.added')}</Button>
                  )}
                </>
              )}
            </Space>

            {/* Profitability Summary */}
            {Object.keys(profitData).length > 0 && (
              <Card title={t('idea_calc.shared_view.section_profitability')} size="small">
                <Row gutter={[16, 16]}>
                  <Col xs={12} sm={8} md={6}>
                    <Statistic
                      title={<><DollarOutlined /> {t('idea_calc.shared_view.stat_profit_per_unit')}</>}
                      value={profitData.profit_per_unit_with_costs as number}
                      precision={2}
                      prefix="$"
                      valueStyle={{ color: (profitData.profit_per_unit_with_costs as number) >= 0 ? c.success : c.error }}
                    />
                  </Col>
                  <Col xs={12} sm={8} md={6}>
                    <Statistic
                      title={<><PercentageOutlined /> {t('idea_calc.shared_view.stat_margin')}</>}
                      value={profitData.batch_margin_percentage as number}
                      precision={1}
                      suffix="%"
                      valueStyle={{ color: (profitData.batch_margin_percentage as number) >= 20 ? c.success : c.warning }}
                    />
                  </Col>
                  <Col xs={12} sm={8} md={6}>
                    <Statistic
                      title={<><RiseOutlined /> {t('idea_calc.shared_view.stat_roi')}</>}
                      value={profitData.batch_roi as number}
                      precision={1}
                      suffix="%"
                      valueStyle={{ color: (profitData.batch_roi as number) >= 50 ? c.success : c.warning }}
                    />
                  </Col>
                  <Col xs={12} sm={8} md={6}>
                    <Statistic
                      title={<><ShoppingCartOutlined /> {t('idea_calc.shared_view.stat_batch_profit')}</>}
                      value={profitData.total_batch_final_profit_with_costs as number}
                      precision={0}
                      prefix="$"
                      valueStyle={{ color: (profitData.total_batch_final_profit_with_costs as number) >= 0 ? c.success : c.error }}
                    />
                  </Col>
                  {/* Снимки, сохранённые до появления МЗЦ, этого числа не содержат —
                      показываем только то, что реально посчитано, без подстановки нуля. */}
                  {typeof profitData.max_purchase_price === 'number' && (
                    <Col xs={12} sm={8} md={6}>
                      <Statistic
                        title={<><TagOutlined /> {t('idea_calc.shared_view.stat_max_purchase_price')}</>}
                        value={profitData.max_purchase_price}
                        precision={2}
                        prefix="$"
                        valueStyle={{ color: profitData.max_purchase_price >= 0 ? c.info : c.error }}
                      />
                    </Col>
                  )}
                </Row>
                <Divider style={{ margin: '16px 0' }} />
                <Descriptions column={3} size="small">
                  <Descriptions.Item label="COGS">{fmtMoney(profitData.total_cost)}</Descriptions.Item>
                  <Descriptions.Item label="Gross Profit">{fmtMoney(profitData.gross_profit)}</Descriptions.Item>
                  <Descriptions.Item label="Final Profit">{fmtMoney(profitData.final_profit)}</Descriptions.Item>
                  <Descriptions.Item label="Referral Fee">{fmtMoney(profitData.referral_fee)}</Descriptions.Item>
                  <Descriptions.Item label="FBA Fee">{fmtMoney(profitData.fba_fee)}</Descriptions.Item>
                  <Descriptions.Item label="Return Loss">{fmtMoney(profitData.return_loss)}</Descriptions.Item>
                  <Descriptions.Item label={t('idea_calc.shared_view_table.ppc_per_unit_label')}>{fmtMoney(profitData.ppc_cost_per_unit)}</Descriptions.Item>
                  <Descriptions.Item label="PPC Total">{fmtMoney(profitData.ppc_cost_total)}</Descriptions.Item>
                  <Descriptions.Item label="Storage Fee">{fmtMoney(profitData.total_storage_fee)}</Descriptions.Item>
                </Descriptions>
              </Card>
            )}

            {/* Budget */}
            {budgetCategories.length > 0 && (
              <Card
                title={t('idea_calc.shared_view.section_launch_budget')}
                size="small"
                extra={<Typography.Text strong>${budgetTotal.toLocaleString('en-US', { maximumFractionDigits: 0 })}</Typography.Text>}
              >
                <Row gutter={[12, 12]}>
                  {budgetCategories.map((cat) => {
                    const catTotal = unitCosts
                      ? sumCategoryItems(cat.items ?? [], unitCosts.cogs, unitCosts.fullUnitCost)
                      : 0;
                    return (
                      <Col key={cat.id} xs={24} sm={12}>
                        <div style={{ background: c.fillSubtle, borderRadius: 6, padding: '8px 12px', border: `1px solid ${c.borderSubtle}` }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                            <div style={{ width: 10, height: 10, borderRadius: 2, background: cat.color }} />
                            <Typography.Text strong style={{ fontSize: 12 }}>{cat.name}</Typography.Text>
                            <Typography.Text type="secondary" style={{ marginLeft: 'auto', fontSize: 12 }}>
                              ${catTotal.toLocaleString('en-US', { maximumFractionDigits: 0 })}
                            </Typography.Text>
                          </div>
                          {cat.items && cat.items.length > 0 && (
                            <div style={{ fontSize: 11, color: c.textSecondary }}>
                              {cat.items
                                .map((item) => ({
                                  item,
                                  amount: unitCosts
                                    ? calcMarketingAmount(item, unitCosts.cogs, unitCosts.fullUnitCost)
                                    : 0,
                                }))
                                .filter(({ amount }) => amount > 0)
                                .map(({ item, amount }, idx) => (
                                  <div key={idx} style={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <span>{item.name || t('idea_calc.shared_view.no_name')}</span>
                                    <span>${amount.toFixed(0)}</span>
                                  </div>
                                ))}
                            </div>
                          )}
                        </div>
                      </Col>
                    );
                  })}
                </Row>
              </Card>
            )}

            <Card title={t('idea_calc.shared_view.section_calc_params')} size="small">
              <Descriptions column={2} size="small" bordered>
                {calcData.parent_asin ? (
                  <Descriptions.Item label="ASIN">{fmt(calcData.parent_asin)}</Descriptions.Item>
                ) : null}
                <Descriptions.Item label={t('idea_calc.shared_view.row_purchase_price')}>${fmt(calcData.purchase_price)}</Descriptions.Item>
                <Descriptions.Item label={t('idea_calc.shared_view.row_shipping')}>${fmt(calcData.shipping_price)}</Descriptions.Item>
                <Descriptions.Item label={t('idea_calc.shared_view.row_selling_price')}>${fmt(calcData.selling_price)}</Descriptions.Item>
                <Descriptions.Item label="Referral Fee">{fmt(calcData.referral_fee)}%</Descriptions.Item>
                <Descriptions.Item label="FBA Fee">${fmt(calcData.fba_fee)}</Descriptions.Item>
                <Descriptions.Item label="PPC Cost">${fmt(calcData.ppc_cost)}</Descriptions.Item>
                <Descriptions.Item label="Conversion Rate">{fmt(calcData.conversion_rate)}%</Descriptions.Item>
                <Descriptions.Item label="PPC Sales %">{fmt(calcData.ppc_sales_percentage)}%</Descriptions.Item>
                <Descriptions.Item label="Return Rate">{fmt(calcData.return_rate)}%</Descriptions.Item>
                <Descriptions.Item label="Monthly Sales">{fmt(calcData.monthly_sales)}</Descriptions.Item>
                <Descriptions.Item label="Batch Qty">{fmt(calcData.batch_quantity)}</Descriptions.Item>
              </Descriptions>
            </Card>
            {Array.isArray(calcData.competitors) && (calcData.competitors as unknown[]).length > 0 ? (
              <Card title={t('idea_calc.shared_view.section_competitors')} size="small">
                <Typography.Text type="secondary">
                  {(calcData.competitors as { asin: string }[]).map((c) => c.asin).join(', ')}
                </Typography.Text>
              </Card>
            ) : null}
          </Space>
        )}
      </Layout.Content>
    </Layout>
  );
};

export default SharedView;
